import React, { useEffect, useState, useContext } from 'react'
import useTranslate from '../hooks/useTranslate';
import Context from '../context'
import { Link } from 'react-router-dom'
import SummaryApi from '../common'
import service from '../common/HttpService'
import CropDrawer from './CropDrawer'
import { IoGridOutline } from 'react-icons/io5'

const CropSection = () => {
    const { translateText, languageCode } = useContext(Context);
    const t = useTranslate({
        crop: "Crop",
        smartGuide: "Smart Guide",
        info: "Info"
    });
    const [crops, setCrops] = useState([])
    const [translatedCrops, setTranslatedCrops] = useState([])
    const [loading, setLoading] = useState(true)
    const [showAllDrawer, setShowAllDrawer] = useState(false)

    const fetchCrops = async () => {
        try {
            setLoading(true)
            const response = await service.get(SummaryApi.getCrop.url)


            // Flexibly handle various response structures
            const cropsData = response.extraData?.crop || response.data?.crop || response.crop || [];

            if (response.success === "true" || response.success === true) {
                // Ensure image URLs are valid
                const formattedCrops = cropsData.map(crop => {
                    let img = crop.image || crop.crop_image;
                    if (img && typeof img === 'string' && !img.startsWith('https')) {
                        img = `https://fasalshop.com/${img.startsWith('/') ? img.substring(1) : img}`;
                    }
                    return {
                        ...crop,
                        image: img || 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400',
                        name: crop.name || crop.crop_name || "Unknown Crop",
                        id: crop.id || crop._id
                    }
                });
                setCrops(formattedCrops)
            }
        } catch (error) {
            console.error("Error fetching crops:", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchCrops()
    }, []);

    useEffect(() => {
        const translateCrops = async () => {
            if (!languageCode || languageCode === 'en' || !crops.length) {
                setTranslatedCrops(crops);
                return;
            }
            try {
                const translated = await Promise.all(
                    crops.map(async (crop) => ({
                        ...crop,
                        name: await translateText(crop.name, languageCode)
                    }))
                );
                setTranslatedCrops(translated);
            } catch (error) {
                console.error("CropSection translation error:", error);
                setTranslatedCrops(crops);
            }
        };
        translateCrops();
    }, [languageCode, translateText, crops]);
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const limit = isDesktop ? 15 : 11;
    const displayCrops = translatedCrops.slice(0, limit);
    const hasMore = translatedCrops.length > limit;


    return (
        <div className='container mx-auto px-4 my-10  relative'>
            {/* Universal Premium Heading */}
            <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl md:text-2xl font-black text-slate-800 uppercase tracking-tight'>
                    {t.crop} <span className='text-[#00b259]'>{t.smartGuide}</span>
                </h2>
            </div>

            <div className='grid grid-cols-4 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 md:gap-3 lg:gap-4 justify-items-center'>
                {loading ? (
                    new Array(8).fill(null).map((_, index) => (
                        <div key={"cropskeleton" + index} className='flex flex-col items-center animate-pulse'>
                            <div className='w-16 h-16 sm:w-28 sm:h-28 rounded-full bg-slate-100 border border-gray-50'></div>
                            <div className='mt-2 h-2.5 w-12 sm:w-16 bg-slate-100 rounded'></div>
                        </div>
                    ))
                ) : (
                    <>
                        {displayCrops.map((crop) => (
                            <div key={crop.id}>
                                <Link
                                    to={`/crop/${crop.id}`}
                                    className='flex flex-col items-center group cursor-pointer w-fit'
                                >
                                    <div className='relative w-16 h-16 sm:w-28 sm:h-28 rounded-full p-1 bg-white border border-gray-100 shadow-md flex items-center justify-center'>
                                        <img
                                            src={crop.image}
                                            alt={crop.name}
                                            className='w-full h-full object-cover rounded-full'
                                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400' }}
                                        />
                                        <div className='absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
                                            <span className='text-[8px] sm:text-[10px] font-black text-white uppercase tracking-widest'>{t.info}</span>
                                        </div>
                                    </div>
                                    <h3 className='mt-2 text-[10px] sm:text-sm font-black text-slate-600 group-hover:text-[#00b259] transition-colors uppercase tracking-[1px] text-center line-clamp-1'>
                                        {crop.name}
                                    </h3>
                                </Link>
                            </div>
                        ))}

                        {hasMore && (
                            <div
                                onClick={() => setShowAllDrawer(true)}
                                className='flex flex-col items-center group cursor-pointer w-fit'
                            >
                                <div className='relative w-16 h-16 sm:w-28 sm:h-28 rounded-full p-1 bg-white border border-gray-100 shadow-md flex items-center justify-center transition-all group-hover:border-[#00b259]/30 group-hover:shadow-lg'>
                                    <div className='w-full h-full rounded-full bg-slate-50 flex items-center justify-center text-[#00b259] group-hover:bg-[#00b259]/5 transition-colors'>
                                        <IoGridOutline className="w-5 h-5 sm:w-7 sm:h-7" />
                                    </div>
                                </div>
                                <h3 className='mt-2 text-[10px] sm:text-sm font-black text-slate-600 group-hover:text-[#00b259] transition-colors uppercase tracking-[1px] text-center'>
                                    More
                                </h3>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Crops Drawer Modal */}
            <CropDrawer
                isOpen={showAllDrawer}
                onClose={() => setShowAllDrawer(false)}
                crops={translatedCrops}
            />
        </div>
    )
}

export default CropSection
